<template>
  <div class="tw-bg-white tw-shadow-xl tw-rounded-2xl tw-overflow-hidden tw-border tw-border-slate-100">
    <!-- Hero: large visual + key details -->
    <div class="tw-grid md:tw-grid-cols-2 tw-border-b tw-border-slate-100">
      <!-- Visual side -->
      <div class="tw-relative tw-bg-slate-900 tw-text-white tw-min-h-[260px] md:tw-min-h-[320px]">
        <div
          v-if="heroImage"
          class="tw-absolute tw-inset-0"
        >
          <img
            :src="heroImage"
            :alt="heroAlt"
            class="tw-w-full tw-h-full tw-object-cover tw-opacity-90 tw-cursor-zoom-in"
            @click="openLightbox(heroImage, heroAlt)"
          >
        </div>
        <div class="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-slate-900/90 tw-via-slate-900/40 tw-to-slate-900/10" />

        <div class="tw-relative tw-h-full tw-flex tw-flex-col tw-justify-end tw-p-6 md:tw-p-8">
          <p class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-white/10 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-tracking-wide tw-uppercase">
            Quote
            <span class="tw-ml-2 tw-font-semibold">#{{ quoteData.quote.reference }}</span>
          </p>
          <h2 class="tw-mt-3 tw-text-2xl md:tw-text-3xl tw-font-semibold tw-text-white">
            Your quote
          </h2>
          <p class="tw-mt-2 tw-text-sm tw-text-slate-200">
            Processed by
            <span class="tw-font-medium">{{ quoteData.quote.sales_person || 'Unknown' }}</span>
            on {{ formatDate(quoteData.items[0]?.created_at) }}
          </p>
          <p
            v-if="quoteData.items[0]?.created_at !== quoteData.items[0]?.updated_at"
            class="tw-mt-1 tw-text-sm tw-text-slate-300"
          >
            Last updated {{ formatDate(quoteData.items[0]?.updated_at) }}
          </p>
        </div>
      </div>

      <!-- Summary side -->
      <div class="tw-bg-slate-50 tw-p-6 md:tw-p-8 tw-flex tw-flex-col tw-justify-between tw-space-y-4">
        <!-- Customer snapshot -->
        <div class="tw-space-y-3">
          <p class="tw-text-xs tw-font-medium tw-tracking-wide tw-text-slate-500 tw-uppercase">
            Contact details
          </p>
          <div class="tw-space-y-3">
            <div>
              <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase">
                Name
              </p>
              <p class="tw-text-sm tw-font-medium tw-text-slate-900 tw-mt-1">
                {{ quoteData.quote.customer_name || 'N/A' }}
              </p>
            </div>
            <div>
              <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase">
                Email
              </p>
              <p class="tw-text-sm tw-font-medium tw-text-slate-900 tw-mt-1 tw-break-all">
                {{ quoteData.quote.customer_email || 'N/A' }}
              </p>
            </div>
            <div class="tw-grid tw-grid-cols-2 tw-gap-4">
              <div>
                <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase">
                  Phone
                </p>
                <p class="tw-text-sm tw-font-medium tw-text-slate-900 tw-mt-1">
                  {{ quoteData.quote.customer_phone || 'N/A' }}
                </p>
              </div>
              <div>
                <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase">
                  Items
                </p>
                <p class="tw-text-sm tw-font-medium tw-text-slate-900 tw-mt-1">
                  {{ quoteItems.length }} item{{ quoteItems.length === 1 ? '' : 's' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- High-level summary -->
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
          <div class="tw-rounded-lg tw-bg-white tw-p-3 tw-border tw-border-slate-100">
            <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase">
              Total due
            </p>
            <p class="tw-mt-1 tw-text-sm tw-font-semibold tw-text-slate-900">
              {{ formatCurrency(quoteData.quote.total) }}
            </p>
          </div>
          <div
            v-if="!pricesOverridden"
            class="tw-rounded-lg tw-bg-white tw-p-3 tw-border tw-border-slate-100"
          >
            <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase">
              Subtotal (Ex.Vat)
            </p>
            <p class="tw-mt-1 tw-text-blue-500 tw-text-lg tw-font-semibold">
              {{ formatCurrency(subtotalBeforeDiscount) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quote Notes -->
    <div
      v-if="quoteData.quote.notes"
      class="tw-mx-6 md:tw-mx-8 tw-mt-6 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4"
    >
      <p class="tw-text-[11px] tw-font-medium tw-text-amber-700 tw-uppercase tw-mb-1">
        Notes
      </p>
      <p class="tw-text-sm tw-text-amber-900 tw-whitespace-pre-line">
        {{ quoteData.quote.notes }}
      </p>
    </div>

    <!-- Quote Attachments -->
    <div
      v-if="quoteData.quote.attachments && quoteData.quote.attachments.length"
      class="tw-mx-6 md:tw-mx-8 tw-mt-6 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4"
    >
      <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase tw-mb-2">
        Attachments
      </p>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
        <a
          v-for="(file, index) in quoteData.quote.attachments"
          :key="index"
          :href="file.url"
          target="_blank"
          class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 hover:tw-border-blue-200 hover:tw-shadow-sm tw-transition tw-text-slate-900 hover:tw-no-underline"
        >
          <!-- Attachment Icon -->
          <svg
            class="tw-w-5 tw-h-5 tw-text-slate-400 tw-flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span class="tw-text-xs tw-font-semibold tw-truncate tw-flex-1">
            {{ file.name }}
          </span>
          <svg
            class="tw-w-4 tw-h-4 tw-text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </div>
    </div>

    <!-- Quote Items -->
    <div class="tw-px-6 md:tw-px-8 tw-py-6">
      <div class="tw-flex tw-items-baseline tw-justify-between tw-mb-4">
        <h4 class="tw-text-lg tw-font-semibold tw-text-slate-900">
          Items in this quote
        </h4>
        <p class="tw-text-xs tw-text-slate-500">
          {{ totalItemQuantity }} item{{ totalItemQuantity === 1 ? '' : 's' }}
        </p>
      </div>

      <div class="tw-space-y-4">
        <div
          v-for="node in itemTree"
          :key="node.item.id"
          class="tw-space-y-2"
        >
          <!-- Parent item -->
        <div
          class="tw-border tw-border-slate-200 tw-rounded-xl tw-p-5 tw-bg-white tw-flex tw-flex-col tw-gap-4 hover:tw-border-blue-200 hover:tw-shadow-sm tw-transition"
        >
          <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
            <!-- Item Image -->
            <div class="tw-flex-shrink-0 tw-w-full sm:tw-w-48">
              <div
                v-if="node.item.image"
                class="tw-w-full tw-h-56 sm:tw-h-48 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-border tw-border-slate-200 tw-p-2"
              >
                <img
                  :src="node.item.image"
                  :alt="node.item.name"
                  class="tw-w-full tw-h-full tw-object-contain tw-cursor-zoom-in"
                  @click="openLightbox(node.item.image, node.item.name)"
                >
              </div>
              <div
                v-else
                class="tw-w-full tw-h-40 sm:tw-h-40 tw-rounded-xl tw-bg-slate-100 tw-flex tw-items-center tw-justify-center"
              >
                <svg
                  class="tw-w-10 tw-h-10 tw-text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <!-- Item Details -->
            <div class="tw-flex-1 tw-flex tw-flex-col tw-justify-between">
              <div>
                <h5 class="tw-text-base tw-font-semibold tw-text-slate-900">
                  {{ node.item.name }}
                </h5>
                <div
                  v-if="node.item.description"
                  class="tw-relative"
                >
                  <div
                    :class="[
                      'tw-text-sm tw-text-slate-600 tw-mt-2 tw-leading-relaxed tw-overflow-hidden tw-transition-all tw-duration-300',
                      expandedItems[node.item.id] ? '' : 'tw-max-h-32'
                    ]"
                    v-html="node.item.description"
                  />
                  <button
                    type="button"
                    class="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-mt-1 tw-font-medium"
                    @click="toggleDescription(node.item.id)"
                  >
                    {{ expandedItems[node.item.id] ? 'Show less' : 'Show more' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Item Price -->
            <div
              v-if="!pricesOverridden"
              class="tw-text-right tw-pt-2 sm:tw-pt-0 sm:tw-pl-4 tw-min-w-[140px]"
            >
              <!-- Show discount if applicable -->
              <div v-if="node.item.discount_amount && node.item.discount_amount < 0">
                <p class="tw-text-xs tw-text-slate-400 tw-line-through">
                  {{ formatCurrency(node.item.price * node.item.quantity) }}
                </p>
                <span class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[11px] tw-font-medium tw-bg-red-50 tw-text-red-700 tw-mb-1">
                  {{ formatCurrency(node.item.discount_amount * node.item.quantity) }} discount
                </span>
                <p class="tw-text-xl tw-font-semibold tw-text-slate-900">
                  {{ formatCurrency(calculateItemTotal(node.item)) }}
                </p>
              </div>

              <!-- Show uplift quietly (just add to price) -->
              <div v-else-if="node.item.discount_amount && node.item.discount_amount > 0">
                <p class="tw-text-xl tw-font-semibold tw-text-slate-900">
                  {{ formatCurrency(calculateItemTotal(node.item)) }}
                </p>
              </div>

              <!-- Normal price (no discount/uplift) -->
              <div v-else>
                <p class="tw-text-xl tw-font-semibold tw-text-slate-900">
                  {{ formatCurrency(node.item.price * node.item.qty) }}
                </p>
              </div>

              <!-- Unit price if quantity > 1 -->
              <p
                v-if="node.item.quantity > 1"
                class="tw-text-xs tw-text-slate-500 tw-mt-1"
              >
                <span v-if="node.item.discount_amount">
                  {{ formatCurrency(parseFloat(node.item.price) + parseFloat(node.item.discount_amount || 0)) }} each
                </span>
                <span v-else>
                  {{ formatCurrency(node.item.price) }} each
                </span>
              </p>
            </div>
          </div>

          <!-- Per-product promotions -->
          <div
            v-if="productPromotions(node.item).length"
            class="tw-pt-3 tw-border-t tw-border-dashed tw-border-slate-200"
          >
            <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase tw-mb-2">
              Recommended add-ons
            </p>
            <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-2">
              <div
                v-for="promo in productPromotions(node.item)"
                :key="promo.id"
                class="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-select-none"
                :class="isAddonSelected(node.item.id, promo.id)
                  ? 'tw-border-blue-400 tw-bg-blue-50 tw-ring-1 tw-ring-blue-400'
                  : 'tw-border-emerald-200 tw-bg-emerald-50 hover:tw-border-emerald-300'"
                @click="toggleAddon(node.item, promo)"
              >
                <div class="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                  <input
                    type="checkbox"
                    :checked="isAddonSelected(node.item.id, promo.id)"
                    class="tw-h-3.5 tw-w-3.5 tw-text-blue-600 tw-border-slate-300 tw-rounded tw-flex-shrink-0 tw-pointer-events-none"
                    tabindex="-1"
                  >
                  <div class="tw-text-left tw-min-w-0">
                    <p
                      class="tw-text-xs tw-font-semibold tw-truncate"
                      :class="isAddonSelected(node.item.id, promo.id) ? 'tw-text-blue-900' : 'tw-text-emerald-900'"
                    >
                      {{ promo.label }}
                    </p>
                    <p
                      v-if="promo.subtitle"
                      class="tw-text-[11px] tw-mt-0.5 tw-truncate"
                      :class="isAddonSelected(node.item.id, promo.id) ? 'tw-text-blue-700' : 'tw-text-emerald-700'"
                    >
                      {{ promo.subtitle }}
                    </p>
                  </div>
                </div>
                <div
                  class="tw-ml-3 tw-text-xs tw-font-semibold tw-whitespace-nowrap"
                  :class="isAddonSelected(node.item.id, promo.id) ? 'tw-text-blue-900' : 'tw-text-emerald-900'"
                >
                  {{ formatCurrency(promo.price) }}
                </div>
              </div>
            </div>
          </div>
        </div>

          <!-- Child items -->
          <div
            v-for="child in node.children"
            :key="child.id"
            class="tw-ml-8 tw-border tw-border-slate-200 tw-border-l-4 tw-border-l-blue-300 tw-rounded-xl tw-p-5 tw-bg-slate-50 tw-flex tw-flex-col tw-gap-4 hover:tw-border-blue-200 hover:tw-shadow-sm tw-transition"
          >
            <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
              <!-- Child Item Image -->
              <div class="tw-flex-shrink-0 tw-w-full sm:tw-w-40">
                <div
                  v-if="child.image"
                  class="tw-w-full tw-h-44 sm:tw-h-40 tw-rounded-xl tw-overflow-hidden tw-bg-white tw-border tw-border-slate-200 tw-p-2"
                >
                  <img
                    :src="child.image"
                    :alt="child.name"
                    class="tw-w-full tw-h-full tw-object-contain tw-cursor-zoom-in"
                    @click="openLightbox(child.image, child.name)"
                  >
                </div>
                <div
                  v-else
                  class="tw-w-full tw-h-32 sm:tw-h-32 tw-rounded-xl tw-bg-slate-100 tw-flex tw-items-center tw-justify-center"
                >
                  <svg
                    class="tw-w-8 tw-h-8 tw-text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <!-- Child Item Details -->
              <div class="tw-flex-1 tw-flex tw-flex-col tw-justify-between">
                <div>
                  <h5 class="tw-text-sm tw-font-semibold tw-text-slate-800">
                    {{ child.name }}
                  </h5>
                  <div
                    v-if="child.description"
                    class="tw-relative"
                  >
                    <div
                      :class="[
                        'tw-text-sm tw-text-slate-600 tw-mt-2 tw-leading-relaxed tw-overflow-hidden tw-transition-all tw-duration-300',
                        expandedItems[child.id] ? '' : 'tw-max-h-32'
                      ]"
                      v-html="child.description"
                    />
                    <button
                      type="button"
                      class="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-mt-1 tw-font-medium"
                      @click="toggleDescription(child.id)"
                    >
                      {{ expandedItems[child.id] ? 'Show less' : 'Show more' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Child Item Price -->
              <div
                v-if="!pricesOverridden"
                class="tw-text-right tw-pt-2 sm:tw-pt-0 sm:tw-pl-4 tw-min-w-[120px]"
              >
                <div v-if="child.discount_amount && child.discount_amount < 0">
                  <p class="tw-text-xs tw-text-slate-400 tw-line-through">
                    {{ formatCurrency(child.price * child.quantity) }}
                  </p>
                  <span class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[11px] tw-font-medium tw-bg-red-50 tw-text-red-700 tw-mb-1">
                    {{ formatCurrency(child.discount_amount * child.quantity) }} discount
                  </span>
                  <p class="tw-text-lg tw-font-semibold tw-text-slate-900">
                    {{ formatCurrency(calculateItemTotal(child)) }}
                  </p>
                </div>
                <div v-else-if="child.discount_amount && child.discount_amount > 0">
                  <p class="tw-text-lg tw-font-semibold tw-text-slate-900">
                    {{ formatCurrency(calculateItemTotal(child)) }}
                  </p>
                </div>
                <div v-else>
                  <p class="tw-text-lg tw-font-semibold tw-text-slate-900">
                    {{ formatCurrency(child.price * child.qty) }}
                  </p>
                </div>
                <p
                  v-if="child.quantity > 1"
                  class="tw-text-xs tw-text-slate-500 tw-mt-1"
                >
                  <span v-if="child.discount_amount">
                    {{ formatCurrency(parseFloat(child.price) + parseFloat(child.discount_amount || 0)) }} each
                  </span>
                  <span v-else>
                    {{ formatCurrency(child.price) }} each
                  </span>
                </p>
              </div>
            </div>

            <!-- Child per-product promotions -->
            <div
              v-if="productPromotions(child).length"
              class="tw-pt-3 tw-border-t tw-border-dashed tw-border-slate-200"
            >
              <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase tw-mb-2">
                Recommended add-ons
              </p>
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-2">
                <div
                  v-for="promo in productPromotions(child)"
                  :key="promo.id"
                  class="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-select-none"
                  :class="isAddonSelected(child.id, promo.id)
                    ? 'tw-border-blue-400 tw-bg-blue-50 tw-ring-1 tw-ring-blue-400'
                    : 'tw-border-emerald-200 tw-bg-emerald-50 hover:tw-border-emerald-300'"
                  @click="toggleAddon(child, promo)"
                >
                  <div class="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                    <input
                      type="checkbox"
                      :checked="isAddonSelected(child.id, promo.id)"
                      class="tw-h-3.5 tw-w-3.5 tw-text-blue-600 tw-border-slate-300 tw-rounded tw-flex-shrink-0 tw-pointer-events-none"
                      tabindex="-1"
                    >
                    <div class="tw-text-left tw-min-w-0">
                      <p
                        class="tw-text-xs tw-font-semibold tw-truncate"
                        :class="isAddonSelected(child.id, promo.id) ? 'tw-text-blue-900' : 'tw-text-emerald-900'"
                      >
                        {{ promo.label }}
                      </p>
                      <p
                        v-if="promo.subtitle"
                        class="tw-text-[11px] tw-mt-0.5 tw-truncate"
                        :class="isAddonSelected(child.id, promo.id) ? 'tw-text-blue-700' : 'tw-text-emerald-700'"
                      >
                        {{ promo.subtitle }}
                      </p>
                    </div>
                  </div>
                  <div
                    class="tw-ml-3 tw-text-xs tw-font-semibold tw-whitespace-nowrap"
                    :class="isAddonSelected(child.id, promo.id) ? 'tw-text-blue-900' : 'tw-text-emerald-900'"
                  >
                    {{ formatCurrency(promo.price) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quote Summary -->
    <div class="tw-bg-slate-900 tw-text-slate-50 tw-px-6 md:tw-px-8 tw-py-5">
      <!-- Prices overridden: just show the quote total -->
      <div
        v-if="pricesOverridden"
        class="tw-flex tw-flex-col tw-gap-1"
      >
        <div class="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
          <span class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-300">Subtotal</span>
          <span class="tw-text-sm tw-font-medium">{{ formatCurrency(quoteData.quote.total) }}</span>
        </div>
        <div
v-if="voucherDiscount > 0"
class="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-emerald-300"
>
          <span class="tw-text-xs tw-uppercase tw-tracking-wide">Voucher discount</span>
          <span class="tw-text-sm tw-font-medium">-{{ formatCurrency(voucherDiscount) }}</span>
        </div>
        <div class="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-pt-2 tw-border-t tw-border-slate-800">
          <span class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-300">Amount due</span>
          <span class="tw-text-2xl md:tw-text-3xl tw-font-semibold tw-text-emerald-400">
            {{ formatCurrency(finalAmountDue) }}
          </span>
        </div>
      </div>

      <div
        v-else
        class="tw-flex tw-flex-col tw-gap-1"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <span class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-300">Subtotal</span>
          <span class="tw-text-sm tw-font-medium">{{ formatCurrency(subtotalBeforeDiscount) }}</span>
        </div>
        <div
          v-if="totalDiscount !== 0"
          class="tw-flex tw-items-center tw-justify-between tw-gap-3"
          :class="totalDiscount < 0 ? 'tw-text-red-300' : 'tw-text-emerald-300'"
        >
          <span class="tw-text-xs tw-uppercase tw-tracking-wide">
            {{ totalDiscount < 0 ? 'Total discount' : 'Total adjustment' }}
          </span>
          <span class="tw-text-sm tw-font-medium">{{ formatCurrency(totalDiscount) }}</span>
        </div>
        <div
v-if="voucherDiscount > 0"
class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-emerald-300"
>
          <span class="tw-text-xs tw-uppercase tw-tracking-wide">Voucher discount</span>
          <span class="tw-text-sm tw-font-medium">-{{ formatCurrency(voucherDiscount) }}</span>
        </div>
        <div class="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-pt-2 tw-border-t tw-border-slate-800">
          <span class="tw-text-xs tw-uppercase tw-tracking-wide tw-text-slate-300">Amount due</span>
          <span class="tw-text-2xl md:tw-text-3xl tw-font-semibold tw-text-emerald-400">
            {{ formatCurrency(finalAmountDue) }}
          </span>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="lightbox.open"
        class="tw-fixed tw-inset-0 tw-z-[9999] tw-bg-black/80 tw-flex tw-items-center tw-justify-center tw-p-4"
        @click.self="closeLightbox"
      >
        <button
          type="button"
          class="tw-absolute tw-top-4 tw-right-4 tw-rounded-full tw-bg-white/10 tw-text-white tw-h-10 tw-w-10 tw-flex tw-items-center tw-justify-center hover:tw-bg-white/20"
          aria-label="Close image preview"
          @click="closeLightbox"
        >
          <i class="pi pi-times" />
        </button>

        <img
          :src="lightbox.src"
          :alt="lightbox.alt"
          class="tw-max-h-[90vh] tw-max-w-[90vw] tw-object-contain tw-rounded-lg tw-shadow-2xl"
        >
      </div>
    </teleport>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

export default {
  name: 'QuoteDetails',
  props: {
    quoteData: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    selectedAddons: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['toggle-addon'],
  setup(props, { emit }) {
    const lightbox = ref({
      open: false,
      src: '',
      alt: ''
    })

    // Track expanded state for each item's description
    const expandedItems = ref({})

    const toggleDescription = (itemId) => {
      expandedItems.value[itemId] = !expandedItems.value[itemId]
    }

    const openLightbox = (src, alt = '') => {
      if (!src) return
      lightbox.value = { open: true, src, alt }
      document.body.style.overflow = 'hidden'
    }

    const closeLightbox = () => {
      lightbox.value = { open: false, src: '', alt: '' }
      document.body.style.overflow = ''
    }

    const handleKeydown = (e) => {
      if (e.key === 'Escape' && lightbox.value.open) closeLightbox()
    }

    onMounted(() => window.addEventListener('keydown', handleKeydown))
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
    })

    // Computed property for quote items
    const quoteItems = computed(() => {
      return props.quoteData.items || []
    })

    // Total unit count across all items (respects quantity > 1)
    const totalItemQuantity = computed(() => {
      return quoteItems.value.reduce((total, item) => {
        return total + (parseInt(item.quantity) || 1)
      }, 0)
    })

    // Build tree: group child items (parent_item_type_id != 0) under their parent (matched by item_key_id)
    // parseInt used throughout because the API may return these IDs as strings
    const itemTree = computed(() => {
      const items = quoteItems.value
      const parentItems = items.filter(i => !parseInt(i.parent_item_type_id))
      const childItems = items.filter(i => parseInt(i.parent_item_type_id) > 0)

      return parentItems.map(parent => ({
        item: parent,
        children: childItems.filter(child => parseInt(child.parent_item_type_id) === parseInt(parent.item_key_id))
      }))
    })

    // Hero image: prefer the new main_image field on the quote,
    // fall back to the first item's image if needed.
    const heroImage = computed(() => {
      const main = props.quoteData?.quote?.main_image || ''
      if (main) return main
      return quoteItems.value[0]?.image || ''
    })

    const heroAlt = computed(() => {
      const ref = props.quoteData?.quote?.reference
      if (ref) return `Quote ${ref}`
      return quoteItems.value[0]?.name || 'Quote preview'
    })

    const pricesOverridden = computed(() => {
      return !!props.quoteData?.quote?.prices_overridden
    })
    
    // Calculate subtotal before any discounts
    const subtotalBeforeDiscount = computed(() => {
      return quoteItems.value.reduce((total, item) => {
        const basePrice = parseFloat(item.price) || 0
        const quantity = parseInt(item.quantity) || 1
        return total + (basePrice * quantity)
      }, 0)
    })
    
    // Calculate total discount amount
    const totalDiscount = computed(() => {
      return quoteItems.value.reduce((total, item) => {
        const discountAmount = parseFloat(item.discount_amount) || 0
        const quantity = parseInt(item.quantity) || 1
        return total + (discountAmount * quantity)
      }, 0)
    })
    
    // Calculate the final total
    const calculatedTotal = computed(() => {
      return quoteItems.value.reduce((total, item) => {
        return total + calculateItemTotal(item)
      }, 0)
    })

    const voucherDiscount = computed(() => {
      return parseFloat(props.quoteData?.quote?.voucher_discount) || 0
    })

    const finalAmountDue = computed(() => {
      const base = parseFloat(props.quoteData?.quote?.total || 0)
      return Math.max(0, base - voucherDiscount.value)
    })

    // Calculate item total with discount/uplift
    const calculateItemTotal = (item) => {
      const basePrice = parseFloat(item.price) || 0
      const discountAmount = parseFloat(item.discount_amount) || 0
      const quantity = parseInt(item.qty) || 1
      
      // discount_amount is per unit, so add it to the unit price then multiply by quantity
      const adjustedUnitPrice = basePrice + discountAmount
      return adjustedUnitPrice * quantity
    }

    // Example per-product promotions (placeholder – later can be driven by API)
    // Limit to 4, eventually sorted by priority when API supports it
    const productPromotions = (item) => {
      const all = item?.promotions || []
      return all.slice(0, 4)
    }

    const isAddonSelected = (itemId, addonId) => {
      const key = `${itemId}-${addonId}`
      return !!props.selectedAddons[key]
    }

    const toggleAddon = (item, addon) => {
      const key = `${item.id}-${addon.id}`
      const selected = !props.selectedAddons[key]
      emit('toggle-addon', { itemId: item.id, addon, selected, key })
    }

    // Format currency
    const formatCurrency = (amount) => {
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(numericAmount || 0)
    }

    // Format date
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(dateString).toLocaleDateString('en-GB', options)
    }

    return {
      lightbox,
      openLightbox,
      closeLightbox,
      quoteItems,
      totalItemQuantity,
      itemTree,
      heroImage,
      heroAlt,
      pricesOverridden,
      subtotalBeforeDiscount,
      totalDiscount,
      calculatedTotal,
      voucherDiscount,
      finalAmountDue,
      calculateItemTotal,
      productPromotions,
      isAddonSelected,
      toggleAddon,
      formatCurrency,
      formatDate,
      expandedItems,
      toggleDescription,
    }
  }
}
</script>
